import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Tooltip, TooltipPositions, useGameStateSlice, useMedia, useModifiers, useBetSlice } from '@pp/core';
import styles from './SpinResult.module.css';
import { BetMapping } from '../../../config/bets';
import { useOnBetCallbackConnected } from '../../gameGrid/hooks/useOnBetCallbackConnected';
import { useGameDispatch } from '../../../store/GameStore';
import { mwGameGridActions } from '../../../store/slices/mwGameGrid/slice';
import { useMWGameGridSlice } from '../../../store/slices/mwGameGrid/selectors';
import { MWTimings } from '../../../config/MWTimings';
import { useMWStateSlice } from '../../../store/slices/mwState/selectors';

import { RecentResult } from './RecentResult';
export interface SpinResultProp {
  id?: string;
  className?: string;
  type?: string;
  active?: boolean;
  multiplier?: number;
  isMulti?: boolean;
  isStatModal?: boolean;
}

export const SpinResult: FunctionComponent<SpinResultProp> = ({
  id,
  className,
  type,
  active,
  multiplier,
  isMulti,
  isStatModal,
}) => {
  const onBet = useOnBetCallbackConnected();
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const balance = useGameStateSlice.getDeductibleBalance();
  const isLowBalance = useBetSlice.isLowBalance(balance);
  const typeClass = type?.split(' ', 1)[0].toLowerCase();
  const multiplierVal = multiplier ? multiplier + 'x' : undefined;
  const text = +(typeClass as never) > 0 ? typeClass : multiplierVal;
  const [disableTooltip, setDisableTooltip] = useState(false);
  const hoverActiveNumber = useMWGameGridSlice.getHoverActiveNumber();
  const isTouchDevice: boolean = useMedia(
    '(hover: none),' + '(pointer: coarse),' + 'navigator.maxTouchPoints > 0,' + `('ontouchstart' in window)`
  );
  const result = useMWStateSlice.getGameResult();
  const dispatch = useGameDispatch();
  const handleRelease = (text: any): void => {
    if (isBetsOpenStatus && !isStatModal && !isTouchDevice) {
      onBet(BetMapping[text]);
      window.navigator.vibrate?.([100]);
    } else {
      showTooltip(MWTimings.BetsBlosedTooltipVisibility);
    }
  };
  const mods = useModifiers(styles, 'recentResult', {
    isbetOpen: isBetsOpenStatus && !isLowBalance && !isStatModal,
    result: !!result,
    isStatModal: isStatModal,
    active: active,
  });

  const handleOnMouseOver = (number: number, mouseLeave: boolean) => {
    if (!isBetsOpenStatus || isStatModal || isTouchDevice) return;
    if (!mouseLeave) {
      dispatch(mwGameGridActions.setHoverActiveNumber({ activeNumber: number }));
    } else {
      dispatch(mwGameGridActions.setHoverActiveNumber({ activeNumber: 0 }));
    }
  };

  const showTooltip = (timer: number) => {
    setDisableTooltip(false);
    setTimeout(() => {
      setDisableTooltip(true);
    }, timer);
  };

  useEffect(() => {
    setDisableTooltip(!isBetsOpenStatus);
    if (!isBetsOpenStatus) {
      dispatch(mwGameGridActions.setHoverActiveNumber({ activeNumber: 0 }));
    }
  }, [isBetsOpenStatus]);

  const shouldGlow = !isTouchDevice && isBetsOpenStatus && !isStatModal && hoverActiveNumber === Number(text);
  const ResultElement = (
    <RecentResult
      shouldGlow={shouldGlow}
      className={className}
      multiplier={multiplier}
      isMulti={isMulti}
      typeClass={typeClass}
      text={text}
      handleRelease={handleRelease}
      handleOnMouseOver={handleOnMouseOver}
      mods={mods}
      id={id}
      hoverActiveNumber={hoverActiveNumber}
    />
  );
  const isBetsOpenStatusConditionText = isBetsOpenStatus ? 'MEGAWHEEL_BET_ON_NUMBER' : 'NOMOREBETSTIP';

  const isExtraWideWidth = useMedia('(min-width: 1512px)');
  const multiplierHeight = useMemo(() => {
    return isExtraWideWidth ? 10 : 7;
  }, [isExtraWideWidth]);

  return (
    <Tooltip
      position={TooltipPositions.Top}
      disabled={disableTooltip || isStatModal}
      text={isLowBalance ? 'INSUFFICIENTFUNDS' : isBetsOpenStatusConditionText}
      attrs={[text as string]}
      displayOffset={isMulti && multiplier ? multiplierHeight : 2}
    >
      {ResultElement}
    </Tooltip>
  );
};
